import { MutableRefObject } from 'react';
import { Extension } from '@tiptap/core';
import { DOMSerializer, Fragment, Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import { TEMPLATE_FIELD_DROPDOWN_NAME } from '../../doc-engine/templateFields';
import { isValidDropdownOptions } from '../nodes/templateField/dropdownFieldUtils';
import mapFragment from '../utils/mapFragment';
import type { TTransformFragment } from './PasteRule';

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/documents/src/dropdownOptions.ts
// Only copied html carries it; the saved html keeps a Dropdown's options in the document's field list.
const OPTIONS_ATTRIBUTE = 'data-options';

const parseOptions = (element: HTMLElement): string[] | null => {
  const value = element.getAttribute(OPTIONS_ATTRIBUTE);
  if (value === null) return null;
  try {
    const options: unknown = JSON.parse(value);
    return isValidDropdownOptions(options) ? (options as string[]) : null;
  } catch {
    return null;
  }
};

// A new serializer: the schema's cached one also writes the saved html, which must never carry the options.
const getClipboardSerializer = (
  schema: Schema,
  optionsRef: MutableRefObject<Record<string, string[]>>
): DOMSerializer => {
  const { nodes, marks } = DOMSerializer.fromSchema(schema);
  const toDropdownDOM = nodes[TEMPLATE_FIELD_DROPDOWN_NAME];
  return new DOMSerializer(
    {
      ...nodes,
      [TEMPLATE_FIELD_DROPDOWN_NAME]: (node) => {
        const dom = DOMSerializer.renderSpec(document, toDropdownDOM(node)).dom as HTMLElement;
        const options = optionsRef.current[node.attrs.fieldId];
        if (options) dom.setAttribute(OPTIONS_ATTRIBUTE, JSON.stringify(options));
        return dom;
      },
    },
    marks
  );
};

/** A copied Dropdown takes its whole option list along, also out of a read-only version and into another editor. */
const DropdownOptionsTransport = Extension.create<{ optionsRef: MutableRefObject<Record<string, string[]>> }>({
  name: 'dropdownOptionsTransport',

  addGlobalAttributes() {
    return [
      {
        types: [TEMPLATE_FIELD_DROPDOWN_NAME],
        attributes: {
          transportedOptions: { default: null, rendered: false, parseHTML: parseOptions },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('dropdownOptionsTransport'),
        props: { clipboardSerializer: getClipboardSerializer(this.editor.schema, this.options.optionsRef) },
      }),
    ];
  },
});

// Files each pasted or dropped Dropdown's carried list under its field id, overriding any list there, then drops it.
const consumeTransportedOptions =
  (optionsRef: MutableRefObject<Record<string, string[]>>) =>
  (fragment: Fragment): Fragment =>
    mapFragment(fragment, (node) => {
      const options: string[] | null = node.attrs.transportedOptions ?? null;
      if (node.type.name !== TEMPLATE_FIELD_DROPDOWN_NAME || !options) return node;
      optionsRef.current[node.attrs.fieldId] = [...options];
      return node.type.create({ ...node.attrs, transportedOptions: null }, node.content, node.marks);
    });

// One transform: PasteRule goes on past a failed one, so a separate consumer could file lists under the old ids.
export const withTransportedOptions = (
  renewIds: TTransformFragment,
  optionsRef: MutableRefObject<Record<string, string[]>>
): TTransformFragment => {
  const consume = consumeTransportedOptions(optionsRef);
  return (fragment, schema) => consume(renewIds(fragment, schema));
};

export default DropdownOptionsTransport;
